<template>
  <div class="runner-page">

    <!-- Offline mode banner -->
    <div v-if="effectiveOffline" class="offline-banner">
      <strong>{{ isOffline ? 'Offline' : 'Local backup mode' }}</strong>
      <span>Playing from local cache. Live updates and "now playing" sync are paused.</span>
    </div>

    <!-- Local backup card -->
    <div class="backup-card" :class="{ 'is-ready': cacheMeta.count > 0, 'is-running': backupState === 'running' }">
      <div class="backup-card-head">
        <div class="backup-card-title">
          <strong>Local backup</strong>
          <span v-if="cacheMeta.count > 0" class="backup-status">
            ✓ {{ cacheMeta.count }} tracks · {{ formatBytes(cacheMeta.bytes) }}
            <span v-if="snapshotSavedAtText"> · saved {{ snapshotSavedAtText }}</span>
          </span>
          <span v-else class="backup-status muted">No backup yet</span>
        </div>
        <div class="backup-card-actions">
          <v-btn
            v-if="cacheMeta.count === 0"
            small
            color="primary"
            :loading="backupState === 'running'"
            :disabled="loading || effectiveOffline"
            @click="createBackup"
          >
            Create local backup
          </v-btn>
          <template v-else>
            <v-btn
              small
              outlined
              :loading="backupState === 'running'"
              :disabled="loading || effectiveOffline"
              @click="createBackup"
            >
              Refresh backup
            </v-btn>
            <v-btn small text color="error" :disabled="backupState === 'running'" @click="clearBackup">
              Clear
            </v-btn>
          </template>
          <label class="backup-toggle">
            <input type="checkbox" v-model="useLocalBackup" :disabled="cacheMeta.count === 0" />
            Use local backup
          </label>
        </div>
      </div>
      <div v-if="backupState === 'running'" class="backup-progress">
        <div class="backup-bar" :style="{ width: backupProgressPct + '%' }"></div>
        <div class="backup-progress-text">
          {{ backupProgress.done }} / {{ backupProgress.total }}
          · {{ formatBytes(backupProgress.bytesDone) }}
          <span v-if="backupProgress.failed"> · <span class="backup-failed-count">{{ backupProgress.failed }} failed</span></span>
        </div>
      </div>
    </div>

    <!-- Export every track as one zip, named in lineup order for the sound op. -->
    <div class="export-card">
      <div class="export-info">
        <strong>Download all music</strong>
        <span class="export-hint">One zip · files named in lineup order, e.g. “01 - A-1 - Title (Dancer).mp3”.</span>
      </div>
      <div class="export-actions">
        <v-btn
          small
          color="primary"
          :loading="exportState === 'fetching' || exportState === 'zipping'"
          :disabled="loading || effectiveOffline"
          @click="downloadAllMusicLabeled"
        >
          ⬇ Download all music
        </v-btn>
        <span v-if="exportState === 'fetching'" class="export-progress">
          Fetching {{ exportProgress.done }} / {{ exportProgress.total }}
          <span v-if="exportProgress.failed"> · <span class="backup-failed-count">{{ exportProgress.failed }} failed</span></span>
        </span>
        <span v-else-if="exportState === 'zipping'" class="export-progress">Building zip…</span>
        <span v-else-if="exportState === 'done'" class="export-progress">✓ Downloaded</span>
      </div>
    </div>

    <div v-if="loading" class="muted">Loading…</div>

    <template v-else>
      <div v-if="completedSlots.length" class="completed-bar">
        <button class="completed-toggle" type="button" @click="showCompleted = !showCompleted">
          ✓ {{ completedSlots.length }} completed
          <span class="caret">{{ showCompleted ? '▴' : '▾' }}</span>
        </button>
        <ol v-if="showCompleted" class="slot-list completed-list">
          <li
            v-for="slot in completedSlots"
            :key="'done-' + (slot.rowKey || slot.key)"
            :class="['kind-' + slot.kind, 'is-done']"
          >
            <div class="slot-main">
              <span v-if="slot.block" class="block-chip" :class="'chip-' + slot.block">Block {{ slot.block }}</span>
              <span v-else-if="slot.kind === 'corps'" class="block-chip chip-corps">Corps</span>
              <span class="slot-label">{{ slot.label }}</span>
              <span v-if="slot.songLine" class="slot-song">{{ slot.songLine }}</span>
              <span v-if="slot.subtitle" class="slot-sub">{{ slot.subtitle }}</span>
            </div>
            <div class="slot-actions">
              <v-btn x-small text @click="markCurrent(slot.key)">↺ re-mark</v-btn>
            </div>
          </li>
        </ol>
      </div>

      <ol class="slot-list">
        <li
          v-for="slot in visibleSlots"
          :key="slot.rowKey || slot.key"
          :class="[
            'kind-' + slot.kind,
            slot.block ? 'block-' + slot.block : '',
            { 'is-current': slot.key === currentSlotKey }
          ]"
        >
          <div class="slot-main">
            <span v-if="slot.block" class="block-chip" :class="'chip-' + slot.block">Block {{ slot.block }}</span>
            <span v-else-if="slot.kind === 'corps'" class="block-chip chip-corps">Corps</span>
            <span class="slot-label">
              {{ slot.label }}
              <span v-if="slot.kind === 'intermission' && intermissionElapsedText" class="elapsed-tag">{{ intermissionElapsedText }} elapsed</span>
            </span>
            <span v-if="slot.songLine" class="slot-song">{{ slot.songLine }}</span>
            <span v-if="slot.subtitle" class="slot-sub">{{ slot.subtitle }}</span>
            <div v-if="slot.kind === 'corps' && (corpsCasts[slot.label] || []).length" class="cast-groups">
              <div v-for="group in corpsCasts[slot.label]" :key="group.role || 'default'" class="cast-group">
                <strong v-if="group.role" class="cast-role">{{ group.role }}:</strong>
                {{ group.dancers.join(', ') }}
              </div>
            </div>
          </div>
          <!-- Start-position toggle (always visible — current state + Edit) -->
          <div v-if="notesKey(slot)" class="slot-startpos">
            <template v-if="startPosOpen[notesKey(slot)]">
              <span class="startpos-label">Starts:</span>
              <label class="startpos-radio">
                <input type="radio" :value="'onstage'" v-model="startPosDraft[notesKey(slot)]" />
                🎭 On stage
              </label>
              <label class="startpos-radio">
                <input type="radio" :value="'offstage'" v-model="startPosDraft[notesKey(slot)]" />
                🚪 Off stage
              </label>
              <v-btn x-small text @click="cancelStartPosEditor(slot)">Cancel</v-btn>
              <v-btn x-small color="primary" :loading="!!startPosSaving[notesKey(slot)]" @click="saveStartPos(slot)">Save</v-btn>
            </template>
            <template v-else-if="startPosCurrent(slot)">
              <span class="startpos-chip" :class="'startpos-' + startPosCurrent(slot)">
                {{ startPosLabel(startPosCurrent(slot)) }}
              </span>
              <v-btn x-small text @click="openStartPosEditor(slot)">Edit</v-btn>
              <span v-if="startPosSaved[notesKey(slot)]" class="rec-saved">✓ Saved</span>
            </template>
            <template v-else>
              <v-btn x-small text @click="openStartPosEditor(slot)">+ Mark start position</v-btn>
              <span v-if="startPosSaved[notesKey(slot)]" class="rec-saved">✓ Saved</span>
            </template>
          </div>

          <!-- Lighting/sound notes (collapsed unless opened explicitly) -->
          <div
            v-if="notesKey(slot) && (notesCurrent(slot) || notesOpen[notesKey(slot)])"
            class="slot-notes"
          >
            <template v-if="notesOpen[notesKey(slot)]">
              <textarea
                v-model="notesDraft[notesKey(slot)]"
                class="notes-textarea"
                rows="3"
                placeholder="Lighting / sound notes for this piece…"
              ></textarea>
              <div class="notes-actions">
                <v-btn x-small text @click="cancelNotesEditor(slot)">Cancel</v-btn>
                <v-btn x-small color="primary" :loading="!!notesSaving[notesKey(slot)]" @click="saveNotes(slot)">Save notes</v-btn>
              </div>
            </template>
            <template v-else>
              <div class="notes-label">💡 Lighting / sound</div>
              <div class="notes-text">{{ notesCurrent(slot) }}</div>
              <v-btn x-small text class="notes-edit-btn" @click="openNotesEditor(slot)">Edit</v-btn>
              <span v-if="notesSaved[notesKey(slot)]" class="rec-saved">✓ Saved</span>
            </template>
          </div>
          <div v-else-if="notesKey(slot)" class="slot-add-notes">
            <v-btn x-small text @click="openNotesEditor(slot)">+ Add lighting/sound notes</v-btn>
            <span v-if="notesSaved[notesKey(slot)]" class="rec-saved">✓ Saved</span>
          </div>

          <div class="slot-actions">
            <span v-if="slot.key === currentSlotKey" class="now-tag">NOW</span>
            <!-- Solo pieces with music: play button. Playing = current slot. -->
            <v-btn
              v-if="slot.kind === 'piece' && slot.piece && slot.piece.musicUrl"
              small
              :color="playingPieceId === slot.piece.id ? 'amber darken-2' : 'success'"
              :dark="playingPieceId === slot.piece.id"
              :loading="saving === slot.key"
              @click="togglePlayPiece(slot)"
            >
              {{ playingPieceId === slot.piece.id ? '⏸ Pause' : '▶ Play' }}
            </v-btn>
            <!-- Single-track corps with uploaded music. -->
            <v-btn
              v-else-if="slot.kind === 'corps' && slot.corpsTrack"
              small
              :color="playingCorpsTrackKey === corpsTrackKeyFor(slot) ? 'amber darken-2' : 'success'"
              :dark="playingCorpsTrackKey === corpsTrackKeyFor(slot)"
              :loading="saving === slot.rowKey"
              @click="togglePlayCorpsTrack(slot)"
            >
              {{ playingCorpsTrackKey === corpsTrackKeyFor(slot) ? '⏸ Pause' : '▶ Play' }}
            </v-btn>
            <!-- Multi-track corps: each track gets its own row + own play button. -->
            <v-btn
              v-else-if="slot.kind === 'corps-track'"
              small
              :color="playingCorpsTrackKey === corpsTrackKeyFor(slot) ? 'amber darken-2' : 'success'"
              :dark="playingCorpsTrackKey === corpsTrackKeyFor(slot)"
              :loading="saving === slot.rowKey"
              @click="togglePlayCorpsTrack(slot)"
            >
              {{ playingCorpsTrackKey === corpsTrackKeyFor(slot) ? '⏸ Pause' : '▶ Play' }}
            </v-btn>
            <!-- Intermission gets a dedicated start button. -->
            <v-btn
              v-else-if="slot.kind === 'intermission'"
              small
              :color="slot.key === currentSlotKey ? 'success' : 'primary'"
              :outlined="slot.key !== currentSlotKey"
              :disabled="saving === slot.key"
              @click="markCurrent(slot.key)"
            >
              {{ slot.key === currentSlotKey ? '✓ Intermission running' : '⏱ Start intermission' }}
            </v-btn>
            <!-- Everything else (corps + pieces without music yet): manual mark. -->
            <v-btn
              v-else
              small
              :color="slot.key === currentSlotKey ? 'success' : 'primary'"
              :outlined="slot.key !== currentSlotKey"
              :disabled="saving === slot.key"
              @click="markCurrent(slot.key)"
            >
              {{ slot.key === currentSlotKey ? '✓ On stage' : 'On stage now' }}
            </v-btn>
          </div>
        </li>
      </ol>
    </template>

    <div class="footer-actions">
      <v-btn outlined small color="error" :loading="resetting" @click="reset">Reset to start</v-btn>
    </div>
  </div>
</template>

<script>
import JSZip from 'jszip'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { resolveShowcaseStructure, resolveCorpsInfo } from '@/utils/showcase-structure'
import { db } from '@/firebase'
import {
  getTrack,
  getAllTrackMeta,
  getSnapshot,
  putSnapshot,
  clearAll,
  downloadTracks,
  requestPersistence
} from '@/utils/showrunnerCache'


function formatElapsed (ms) {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total - h * 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

function interleave (a, b) {
  const out = []
  const max = Math.max(a.length, b.length)
  for (let i = 0; i < max; i++) {
    if (i < a.length) out.push(a[i])
    if (i < b.length) out.push(b[i])
  }
  return out
}

function autoOrder (pieces) {
  // Comp/non-comp alternation carries across placement-group boundaries.
  const groups = { beginning: [], middle: [], end: [], unspecified: [] }
  for (const p of pieces) {
    const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
    groups[k].push(p)
  }
  const unspecHalf = Math.ceil(groups.unspecified.length / 2)
  const groupSequence = [
    groups.beginning,
    groups.unspecified.slice(0, unspecHalf),
    groups.middle,
    groups.unspecified.slice(unspecHalf),
    groups.end
  ]
  const isComp = p => !!(p.competition && p.competition !== 'None')
  const out = []
  let lastType = null
  for (const list of groupSequence) {
    const comp = list.filter(isComp)
    const non = list.filter(p => !isComp(p))
    if (!comp.length && !non.length) continue
    let first, second
    if (!comp.length) { first = non; second = [] }
    else if (!non.length) { first = comp; second = [] }
    else if (lastType === 'comp') { first = non; second = comp }
    else if (lastType === 'non') { first = comp; second = non }
    else {
      first = comp.length >= non.length ? comp : non
      second = first === comp ? non : comp
    }
    const interleaved = interleave(first, second)
    out.push(...interleaved)
    if (interleaved.length) {
      lastType = isComp(interleaved[interleaved.length - 1]) ? 'comp' : 'non'
    }
  }
  return out
}

function orderBlockPieces (pieces) {
  if (!pieces.length) return []
  const pinnedNumeric = pieces
    .filter(p => Number.isFinite(p.showcaseOrder) && p.showcaseOrder >= 1)
    .slice()
    .sort((a, b) => a.showcaseOrder - b.showcaseOrder)
  const pinnedLast = pieces.filter(p => p.showcaseOrder === 'last')
  const unpinned = pieces.filter(p => !pinnedNumeric.includes(p) && !pinnedLast.includes(p))
  const N = pieces.length
  const slots = new Array(N).fill(null)
  for (const p of pinnedNumeric) {
    let target = Math.max(1, Math.min(p.showcaseOrder, N)) - 1
    while (target < N && slots[target] !== null) target += 1
    if (target >= N) target = slots.findIndex(s => s === null)
    if (target !== -1) slots[target] = p
  }
  for (const p of pinnedLast) {
    let target = N - 1
    while (target >= 0 && slots[target] !== null) target -= 1
    if (target === -1) target = slots.findIndex(s => s === null)
    if (target !== -1) slots[target] = p
  }
  const isComp = p => !!(p.competition && p.competition !== 'None')
  const groups = { beginning: [], middle: [], end: [], unspecified: [] }
  for (const p of unpinned) {
    const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
    groups[k].push(p)
  }
  const half = Math.ceil(groups.unspecified.length / 2)
  const placementOrder = [
    ...groups.beginning,
    ...groups.unspecified.slice(0, half),
    ...groups.middle,
    ...groups.unspecified.slice(half),
    ...groups.end
  ]
  const compQueue = placementOrder.filter(isComp)
  const nonQueue = placementOrder.filter(p => !isComp(p))
  for (let i = 0; i < N; i++) {
    if (slots[i] !== null) continue
    let prevType = null
    for (let j = i - 1; j >= 0; j--) {
      if (slots[j] !== null) { prevType = isComp(slots[j]) ? 'comp' : 'non'; break }
    }
    let nextType = null
    if (prevType === null) {
      for (let j = i + 1; j < N; j++) {
        if (slots[j] !== null) { nextType = isComp(slots[j]) ? 'comp' : 'non'; break }
      }
    }
    let wantComp
    if (prevType === 'comp') wantComp = false
    else if (prevType === 'non') wantComp = true
    else if (nextType === 'comp') wantComp = false
    else if (nextType === 'non') wantComp = true
    else wantComp = compQueue.length >= nonQueue.length
    if (wantComp && compQueue.length) slots[i] = compQueue.shift()
    else if (!wantComp && nonQueue.length) slots[i] = nonQueue.shift()
    else if (compQueue.length) slots[i] = compQueue.shift()
    else if (nonQueue.length) slots[i] = nonQueue.shift()
  }
  return slots
}

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      pieces: [],
      corpsCasts: {},
      showcaseStructure: [],
      corpsInfoMap: {},
      corpsMusic: {},
      currentSlotKey: null,
      intermissionStartedAt: null,
      now: Date.now(),
      tickHandle: null,
      unsubscribe: null,
      saving: null,
      resetting: false,
      showCompleted: true,
      audio: null,
      currentObjectUrl: null,
      playingPieceId: null,
      playingCorpsTrackKey: null,
      isOffline: !navigator.onLine,
      useLocalBackup: false,
      cacheMeta: { count: 0, bytes: 0 },
      snapshotSavedAt: null,
      backupState: 'idle',
      backupProgress: { done: 0, total: 0, bytesDone: 0 },
      // "Download all music, labeled by lineup" export (single zip).
      exportState: 'idle', // idle | fetching | zipping | done
      exportProgress: { done: 0, total: 0, failed: 0 },
      // Per-piece lighting/sound notes editor state. Keyed by piece id
      // (or 'corps:<field>' for corps pieces). Stays empty unless the
      // user explicitly opens the editor — so a stray click won't
      // overwrite anything.
      notesOpen: {},
      notesDraft: {},
      notesSaving: {},
      notesSaved: {},
      // Per-piece start-position toggle (onstage | offstage). Same
      // "explicit edit mode + Save" pattern as notes so a stray click
      // can't flip it.
      startPosOpen: {},
      startPosDraft: {},
      startPosSaving: {},
      startPosSaved: {}
    }
  },
  computed: {
    slots () {
      const out = []
      for (const slot of (this.showcaseStructure || [])) {
        if (slot.type === 'corps') {
          const entry = this.corpsMusic[slot.name]
          let tracks = []
          if (entry) {
            if (Array.isArray(entry.tracks)) tracks = entry.tracks
            else if (entry.url) tracks = [{ url: entry.url, fileName: entry.fileName, lengthInSeconds: entry.lengthInSeconds }]
          }
          // Map corps name → field key (matches CORPS_INFO in IABFPieces.vue)
          // so per-corps notes can be looked up + saved against the same id.
          const corpsField = ((this.corpsInfoMap || {})[slot.name] || {}).field || ''
          if (tracks.length <= 1) {
            out.push({
              key: `corps:${slot.name}`,
              rowKey: `corps:${slot.name}`,
              kind: 'corps',
              label: slot.name,
              subtitle: '',
              corpsTrack: tracks[0] || null,
              corpsName: slot.name,
              corpsField
            })
          } else {
            // Multi-track corps: render one row per track, all sharing the same
            // slot key so the public lineup still treats it as one corps step.
            tracks.forEach((track, ti) => {
              out.push({
                key: `corps:${slot.name}`,
                rowKey: `corps:${slot.name}:${ti}`,
                kind: 'corps-track',
                label: slot.name,
                subtitle: `Track ${ti + 1}/${tracks.length}${track.fileName ? ` · ${track.fileName}` : ''}`,
                corpsTrack: track,
                corpsName: slot.name,
                corpsField,
                trackIndex: ti
              })
            })
          }
        } else if (slot.type === 'intermission') {
          out.push({ key: 'intermission', kind: 'intermission', label: slot.name, subtitle: '' })
        } else {
          const pieces = orderBlockPieces(this.pieces.filter(p => p.showcaseBlock === slot.block))
          pieces.forEach((p, idx) => {
            const labelTitle = p.programTitle || p.title || '(untitled)'
            // Always show the song name + artist on its own line for the
            // music operator. We include piece.title when it differs from
            // the program label (e.g. the program shows a choreographic
            // title and piece.title is the actual song), and always tack
            // on the artist when we have one.
            const pTitle = String(p.title || '').trim()
            const pProgram = String(p.programTitle || '').trim()
            const pArtist = String(p.songArtist || '').trim()
            const parts = []
            if (pProgram && pTitle && pTitle.toLowerCase() !== pProgram.toLowerCase()) {
              parts.push(pTitle)
            }
            if (pArtist) parts.push('by ' + pArtist)
            const songLine = parts.join(' ')
            const dancer = p.programDancer || p.namedDancers || ''
            out.push({
              key: `piece:${p.id}`,
              kind: 'piece',
              block: slot.block,
              label: `${slot.block}${idx + 1}. ${labelTitle}`,
              subtitle: dancer,
              songLine,
              piece: p
            })
          })
        }
      }
      return out
    },
    currentIndex () {
      if (!this.currentSlotKey) return -1
      return this.slots.findIndex(s => s.key === this.currentSlotKey)
    },
    completedSlots () {
      if (this.currentIndex < 0) return []
      return this.slots.slice(0, this.currentIndex)
    },
    visibleSlots () {
      if (this.currentIndex < 0) return this.slots
      return this.slots.slice(this.currentIndex)
    },
    intermissionElapsedText () {
      if (this.currentSlotKey !== 'intermission') return ''
      if (!this.intermissionStartedAt) return ''
      return formatElapsed(this.now - this.intermissionStartedAt)
    },
    effectiveOffline () {
      return this.isOffline || this.useLocalBackup
    },
    snapshotSavedAtText () {
      if (!this.snapshotSavedAt) return ''
      const d = new Date(this.snapshotSavedAt)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    },
    backupProgressPct () {
      const { done, total } = this.backupProgress
      if (!total) return 0
      return Math.min(100, Math.round((done / total) * 100))
    }
  },
  watch: {
    effectiveOffline (nowOffline, wasOffline) {
      if (nowOffline === wasOffline) return
      if (nowOffline) {
        this.switchToOffline()
      } else {
        this.switchToOnline()
      }
    }
  },
  async created () {
    this.$store.commit('setLayout', 'embed-layout')
    // Year-aware show order (2026 corps only; later years have no hardcoded corps).
    this.showcaseStructure = resolveShowcaseStructure(this.adminFestivalYear)
    this.corpsInfoMap = resolveCorpsInfo(this.adminFestivalYear)
    window.addEventListener('online', this.onOnline)
    window.addEventListener('offline', this.onOffline)
    // Corps lighting/sound notes live in the same config doc as corps
    // performance recordings; subscribe so the editor reflects any
    // other admin's edits.
    this.unsubCorpsRecordings = await this.$store.dispatch('subscribeCorpsRecordings', { useAdminYear: true })
    await this.refreshCacheMeta()
    if (this.effectiveOffline && this.cacheMeta.count > 0) {
      await this.loadFromSnapshot()
    } else {
      try {
        await this.loadFromFirestore()
      } catch (e) {
        console.error('Online load failed, falling back to snapshot:', e)
        if (this.cacheMeta.count > 0) {
          this.isOffline = true
          await this.loadFromSnapshot()
        }
      }
    }
    // Tick the clock once per second so the elapsed-time display updates.
    this.tickHandle = setInterval(() => { this.now = Date.now() }, 1000)
    this.loading = false
  },
  beforeDestroy () {
    window.removeEventListener('online', this.onOnline)
    window.removeEventListener('offline', this.onOffline)
    if (this.unsubscribe) this.unsubscribe()
    if (this.tickHandle) clearInterval(this.tickHandle)
    if (this.audio) {
      try { this.audio.pause() } catch (e) { /* noop */ }
      this.audio = null
    }
    this.revokeCurrentObjectUrl()
  },
  methods: {
    // ─── Lighting/sound notes per slot ─────────────────────────────
    notesKey (slot) {
      if (slot.kind === 'corps' || slot.kind === 'corps-track') {
        return 'corps:' + (slot.corpsField || (slot.label || '').replace(/\s+/g, ''))
      }
      if (slot.piece && slot.piece.id) return 'piece:' + slot.piece.id
      return null
    },
    notesCurrent (slot) {
      if (slot.kind === 'piece' && slot.piece) {
        return String(slot.piece.lightingSoundNotes || '')
      }
      // Corps notes live in the same config doc as corps recordings,
      // exposed under the corpsNotes getter (separate from URLs).
      if (slot.kind === 'corps' || slot.kind === 'corps-track') {
        const field = slot.corpsField || ''
        if (!field) return ''
        return String((this.$store.getters.corpsNotes || {})[field] || '')
      }
      return ''
    },
    openNotesEditor (slot) {
      const key = this.notesKey(slot)
      if (!key) return
      this.$set(this.notesDraft, key, this.notesCurrent(slot))
      this.$set(this.notesOpen, key, true)
    },
    cancelNotesEditor (slot) {
      const key = this.notesKey(slot)
      if (!key) return
      this.$delete(this.notesOpen, key)
      this.$delete(this.notesDraft, key)
    },
    async saveNotes (slot) {
      const key = this.notesKey(slot)
      if (!key) return
      const text = (this.notesDraft[key] || '').trim()
      this.$set(this.notesSaving, key, true)
      try {
        if (slot.kind === 'piece' && slot.piece) {
          await db.collection(this.IABF.pieces).doc(slot.piece.id)
            .update({ lightingSoundNotes: text })
          // Keep local pieces in sync so the read-only view updates
          // without a full reload.
          const p = this.pieces.find(x => x.id === slot.piece.id)
          if (p) this.$set(p, 'lightingSoundNotes', text)
        } else if (slot.kind === 'corps' || slot.kind === 'corps-track') {
          const field = slot.corpsField || ''
          if (!field) throw new Error('No corps field for this slot.')
          await db.collection(this.IABF.config).doc('performance-recordings')
            .set({ notes: { [field]: text } }, { merge: true })
        }
        this.$delete(this.notesOpen, key)
        this.$delete(this.notesDraft, key)
        this.$set(this.notesSaved, key, true)
        setTimeout(() => { this.$delete(this.notesSaved, key) }, 1800)
      } catch (e) {
        console.error('Save notes failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Save failed: ' + (e.message || e))
      }
      this.$delete(this.notesSaving, key)
    },
    // ─── Start-position toggle per slot ────────────────────────────
    startPosCurrent (slot) {
      if (slot.kind === 'piece' && slot.piece) {
        return String(slot.piece.startPosition || '')
      }
      if (slot.kind === 'corps' || slot.kind === 'corps-track') {
        const field = slot.corpsField || ''
        if (!field) return ''
        return String((this.$store.getters.corpsStartPositions || {})[field] || '')
      }
      return ''
    },
    startPosLabel (value) {
      if (value === 'onstage') return '🎭 Starts on stage'
      if (value === 'offstage') return '🚪 Starts off stage'
      return ''
    },
    openStartPosEditor (slot) {
      const key = this.notesKey(slot)
      if (!key) return
      this.$set(this.startPosDraft, key, this.startPosCurrent(slot))
      this.$set(this.startPosOpen, key, true)
    },
    cancelStartPosEditor (slot) {
      const key = this.notesKey(slot)
      if (!key) return
      this.$delete(this.startPosOpen, key)
      this.$delete(this.startPosDraft, key)
    },
    async saveStartPos (slot) {
      const key = this.notesKey(slot)
      if (!key) return
      const value = this.startPosDraft[key] || ''
      this.$set(this.startPosSaving, key, true)
      try {
        if (slot.kind === 'piece' && slot.piece) {
          await db.collection(this.IABF.pieces).doc(slot.piece.id)
            .update({ startPosition: value })
          const p = this.pieces.find(x => x.id === slot.piece.id)
          if (p) this.$set(p, 'startPosition', value)
        } else if (slot.kind === 'corps' || slot.kind === 'corps-track') {
          const field = slot.corpsField || ''
          if (!field) throw new Error('No corps field for this slot.')
          await db.collection(this.IABF.config).doc('performance-recordings')
            .set({ startPositions: { [field]: value } }, { merge: true })
        }
        this.$delete(this.startPosOpen, key)
        this.$delete(this.startPosDraft, key)
        this.$set(this.startPosSaved, key, true)
        setTimeout(() => { this.$delete(this.startPosSaved, key) }, 1800)
      } catch (e) {
        console.error('Save start position failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Save failed: ' + (e.message || e))
      }
      this.$delete(this.startPosSaving, key)
    },
    onOnline () { this.isOffline = false },
    onOffline () { this.isOffline = true },
    async loadFromFirestore () {
      const piecesSnap = await db.collection(this.IABF.pieces).get()
      this.pieces = piecesSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      this.unsubscribe = db.collection(this.IABF.config).doc('showcase-runtime')
        .onSnapshot(doc => {
          const data = doc.exists ? doc.data() : {}
          this.currentSlotKey = data.currentSlotKey || null
          this.intermissionStartedAt = data.intermissionStartedAt || null
          this.corpsCasts = data.corpsCasts || {}
          this.showcaseStructure = resolveShowcaseStructure(this.adminFestivalYear, data)
          this.corpsInfoMap = resolveCorpsInfo(this.adminFestivalYear, data)
          this.corpsMusic = data.corpsMusic || {}
        })
    },
    async loadFromSnapshot () {
      const snap = await getSnapshot()
      if (!snap) return
      this.pieces = snap.pieces || []
      this.corpsCasts = snap.corpsCasts || {}
      this.corpsMusic = snap.corpsMusic || {}
      this.snapshotSavedAt = snap.savedAt || null
      // currentSlotKey/intermission stay local-only in offline mode.
    },
    async switchToOffline () {
      if (this.unsubscribe) {
        this.unsubscribe()
        this.unsubscribe = null
      }
      if (this.cacheMeta.count > 0) {
        await this.loadFromSnapshot()
      }
    },
    async switchToOnline () {
      try {
        await this.loadFromFirestore()
      } catch (e) {
        console.error('Switch-to-online failed:', e)
      }
    },
    async refreshCacheMeta () {
      try {
        const all = await getAllTrackMeta()
        let bytes = 0
        for (const t of all) bytes += t.sizeBytes || 0
        this.cacheMeta = { count: all.length, bytes }
        const snap = await getSnapshot()
        this.snapshotSavedAt = snap && snap.savedAt ? snap.savedAt : null
      } catch (e) {
        console.error('Read cache meta failed:', e)
      }
    },
    collectMusicUrls () {
      const urls = []
      for (const p of this.pieces) {
        if (p.musicUrl) urls.push(p.musicUrl)
      }
      for (const corpsName of Object.keys(this.corpsMusic || {})) {
        const entry = this.corpsMusic[corpsName]
        if (!entry) continue
        if (Array.isArray(entry.tracks)) {
          for (const t of entry.tracks) {
            if (t && t.url) urls.push(t.url)
          }
        } else if (entry.url) {
          urls.push(entry.url)
        }
      }
      return urls
    },
    async createBackup () {
      if (this.backupState === 'running') return
      this.backupState = 'running'
      this.backupProgress = { done: 0, total: 0, bytesDone: 0, succeeded: 0, failed: 0 }
      try {
        await requestPersistence()
        // Snapshot lineup data first — if the wifi dies mid-download, we
        // still have enough to render the lineup.
        await putSnapshot({
          pieces: this.pieces,
          corpsCasts: this.corpsCasts,
          corpsMusic: this.corpsMusic,
          savedAt: Date.now(),
          version: 1
        })
        const urls = this.collectMusicUrls()
        const result = await downloadTracks(urls, {
          concurrency: 4,
          onProgress: (p) => { this.backupProgress = { ...this.backupProgress, ...p } }
        })
        await this.refreshCacheMeta()
        if (result.failed > 0) {
          const sample = result.errors.slice(0, 3).map(e => `• ${e.message}`).join('\n')
          // eslint-disable-next-line no-alert
          window.alert(
            `${result.failed} of ${result.total} tracks failed to download.\n\n` +
            `${result.succeeded} saved successfully.\n\n` +
            `First errors:\n${sample}\n\n` +
            `Open the JS console for the full list. Most likely a CORS issue on Firebase Storage.`
          )
        }
        this.backupState = 'done'
      } catch (e) {
        console.error('Backup failed:', e)
        this.backupState = 'idle'
        // eslint-disable-next-line no-alert
        window.alert('Backup failed: ' + (e && e.message ? e.message : e))
        return
      }
      this.backupState = 'idle'
    },
    // Extract a file extension (with dot) from a filename or URL; default .mp3.
    exportExt (fileName, url) {
      const src = String(fileName || url || '').replace(/[?#].*$/, '')
      const m = src.match(/\.([a-zA-Z0-9]{2,4})$/)
      return m ? '.' + m[1].toLowerCase() : '.mp3'
    },
    // Strip characters illegal in filenames (macOS/Windows) and collapse
    // whitespace. Keeps unicode letters so dancer names stay intact.
    sanitizeExportName (s) {
      return String(s || '')
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    // Walk the ordered lineup and produce one export entry per music file, in
    // show order. Solo pieces: "<code> - <title> (<dancer>)"; corps pieces:
    // "<corps name>". A zero-padded running number is prefixed after, so the
    // files sort in exact lineup order in any file browser.
    buildLabeledExportItems () {
      const raw = []
      const blockCount = {}
      for (const slot of this.slots) {
        if (slot.kind === 'piece' && slot.piece && slot.piece.musicUrl) {
          const block = slot.block || '?'
          blockCount[block] = (blockCount[block] || 0) + 1
          const p = slot.piece
          const title = this.sanitizeExportName(p.programTitle || p.title || 'Untitled')
          const dancer = this.sanitizeExportName(slot.subtitle || '')
          const base = dancer ? `${block}-${blockCount[block]} - ${title} (${dancer})` : `${block}-${blockCount[block]} - ${title}`
          raw.push({ url: p.musicUrl, base, ext: this.exportExt(p.musicFileName, p.musicUrl) })
        } else if (slot.kind === 'corps' && slot.corpsTrack && slot.corpsTrack.url) {
          const name = this.sanitizeExportName(slot.corpsName || slot.label || 'Corps')
          raw.push({ url: slot.corpsTrack.url, base: name, ext: this.exportExt(slot.corpsTrack.fileName, slot.corpsTrack.url) })
        } else if (slot.kind === 'corps-track' && slot.corpsTrack && slot.corpsTrack.url) {
          const name = this.sanitizeExportName(slot.corpsName || slot.label || 'Corps')
          raw.push({ url: slot.corpsTrack.url, base: `${name} (Track ${(slot.trackIndex || 0) + 1})`, ext: this.exportExt(slot.corpsTrack.fileName, slot.corpsTrack.url) })
        }
      }
      const width = Math.max(2, String(raw.length).length)
      return raw.map((it, i) => ({
        url: it.url,
        fileName: `${String(i + 1).padStart(width, '0')} - ${it.base}${it.ext}`
      }))
    },
    async downloadAllMusicLabeled () {
      if (this.exportState === 'fetching' || this.exportState === 'zipping') return
      const items = this.buildLabeledExportItems()
      if (!items.length) {
        // eslint-disable-next-line no-alert
        window.alert('No music files found in the lineup yet.')
        return
      }
      this.exportState = 'fetching'
      this.exportProgress = { done: 0, total: items.length, failed: 0 }
      const zip = new JSZip()
      const errors = []
      const usedNames = new Set()
      const queue = items.slice()
      const worker = async () => {
        while (queue.length) {
          const item = queue.shift()
          if (!item) break
          try {
            const res = await fetch(item.url, { cache: 'no-store' })
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const blob = await res.blob()
            // De-dupe identical filenames (e.g. two pieces with the same title).
            let name = item.fileName
            if (usedNames.has(name)) {
              const dot = name.lastIndexOf('.')
              const stem = dot > 0 ? name.slice(0, dot) : name
              const ext = dot > 0 ? name.slice(dot) : ''
              let n = 2
              while (usedNames.has(`${stem} (${n})${ext}`)) n += 1
              name = `${stem} (${n})${ext}`
            }
            usedNames.add(name)
            zip.file(name, blob)
          } catch (e) {
            this.exportProgress.failed += 1
            errors.push({ name: item.fileName, message: (e && e.message) ? e.message : String(e) })
            console.error('Music export failed for', item.fileName, item.url, e)
          }
          this.exportProgress.done += 1
        }
      }
      try {
        await Promise.all(Array.from({ length: Math.min(4, items.length) }, () => worker()))
        this.exportState = 'zipping'
        // mp3s are already compressed, so STORE (no re-compression) keeps this
        // near-instant and low-CPU.
        const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'STORE' })
        const url = URL.createObjectURL(zipBlob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'IABF-2026-showcase-music.zip'
        a.click()
        URL.revokeObjectURL(url)
        this.exportState = 'done'
        if (errors.length) {
          const sample = errors.slice(0, 3).map(e => `• ${e.name}: ${e.message}`).join('\n')
          // eslint-disable-next-line no-alert
          window.alert(
            `${errors.length} of ${items.length} tracks couldn't be fetched and were left out of the zip.\n\n` +
            `The other ${items.length - errors.length} are in the download.\n\n` +
            `First errors:\n${sample}\n\nOpen the JS console for the full list.`
          )
        }
      } catch (e) {
        console.error('Music export failed:', e)
        this.exportState = 'idle'
        // eslint-disable-next-line no-alert
        window.alert('Download failed: ' + ((e && e.message) ? e.message : e))
        return
      }
      setTimeout(() => { if (this.exportState === 'done') this.exportState = 'idle' }, 4000)
    },
    async clearBackup () {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Clear local backup? You will need to re-download all music.')) return
      try {
        await clearAll()
        this.cacheMeta = { count: 0, bytes: 0 }
        this.snapshotSavedAt = null
        this.useLocalBackup = false
      } catch (e) {
        console.error('Clear failed:', e)
      }
    },
    formatBytes (n) {
      if (!n) return '0 B'
      const mb = n / (1024 * 1024)
      if (mb >= 1) return mb.toFixed(1) + ' MB'
      const kb = n / 1024
      return kb.toFixed(0) + ' KB'
    },
    revokeCurrentObjectUrl () {
      if (this.currentObjectUrl) {
        try { URL.revokeObjectURL(this.currentObjectUrl) } catch (e) { /* noop */ }
        this.currentObjectUrl = null
      }
    },
    async getPlayableSrc (url) {
      this.revokeCurrentObjectUrl()
      if (!url) return null
      try {
        const cached = await getTrack(url)
        if (cached && cached.blob) {
          const objectUrl = URL.createObjectURL(cached.blob)
          this.currentObjectUrl = objectUrl
          return objectUrl
        }
      } catch (e) {
        console.error('Cache lookup failed:', e)
      }
      return url
    },
    corpsTrackKeyFor (slot) {
      const idx = slot.trackIndex == null ? 0 : slot.trackIndex
      return `${slot.corpsName}:${idx}`
    },
    async togglePlayCorpsTrack (slot) {
      if (!slot || !slot.corpsTrack) return
      const trackKey = this.corpsTrackKeyFor(slot)
      if (this.playingCorpsTrackKey === trackKey) {
        if (this.audio) {
          try { this.audio.pause() } catch (e) { /* noop */ }
        }
        this.playingCorpsTrackKey = null
        return
      }
      this.ensureAudio()
      try { this.audio.pause() } catch (e) { /* noop */ }
      const src = await this.getPlayableSrc(slot.corpsTrack.url)
      if (!src) return
      this.audio.src = src
      this.audio.currentTime = 0
      this.saving = slot.rowKey
      const markPromise = this.markCurrentSlotKey(slot.key, null)
      try {
        await this.audio.play()
        this.playingCorpsTrackKey = trackKey
        this.playingPieceId = null
      } catch (e) {
        console.error('audio play failed:', e)
        this.playingCorpsTrackKey = null
      }
      await markPromise
      this.saving = null
    },
    ensureAudio () {
      if (this.audio) return
      this.audio = new Audio()
      this.audio.addEventListener('ended', () => {
        // No auto-advance on the show runner — the runner decides what's next.
        this.playingPieceId = null
        this.playingCorpsTrackKey = null
        this.revokeCurrentObjectUrl()
      })
      this.audio.addEventListener('error', () => {
        this.playingPieceId = null
        this.playingCorpsTrackKey = null
        this.revokeCurrentObjectUrl()
      })
    },
    async togglePlayPiece (slot) {
      if (!slot || !slot.piece || !slot.piece.musicUrl) return
      const piece = slot.piece
      // Toggle pause on the currently playing piece.
      if (this.playingPieceId === piece.id) {
        if (this.audio) {
          try { this.audio.pause() } catch (e) { /* noop */ }
        }
        this.playingPieceId = null
        return
      }
      this.ensureAudio()
      try { this.audio.pause() } catch (e) { /* noop */ }
      this.playingCorpsTrackKey = null
      const src = await this.getPlayableSrc(piece.musicUrl)
      if (!src) return
      this.audio.src = src
      this.audio.currentTime = 0
      this.saving = slot.rowKey || slot.key
      const markPromise = this.markCurrentSlotKey(slot.key, null)
      try {
        await this.audio.play()
        this.playingPieceId = piece.id
      } catch (e) {
        console.error('audio play failed:', e)
        this.playingPieceId = null
      }
      await markPromise
      this.saving = null
    },
    async markCurrentSlotKey (key, intermissionStartedAt) {
      // Local update always — keeps the on-stage cursor moving in offline mode.
      this.currentSlotKey = key
      this.intermissionStartedAt = intermissionStartedAt
      if (this.effectiveOffline) return
      try {
        await this.$store.dispatch('setCurrentSlot', {
          slotKey: key,
          intermissionStartedAt
        })
      } catch (e) {
        console.error('setCurrentSlot failed:', e)
      }
    },
    async markCurrent (key) {
      this.saving = key
      // Stamp the timestamp when intermission begins; clear it otherwise so
      // an old timer doesn't reappear later in the show.
      const intermissionStartedAt = key === 'intermission' ? Date.now() : null
      await this.markCurrentSlotKey(key, intermissionStartedAt)
      this.saving = null
    },
    async reset () {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Reset the show to the start?')) return
      this.resetting = true
      await this.markCurrentSlotKey(null, null)
      this.resetting = false
    }
  }
}
</script>

<style scoped>
.runner-page {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.offline-banner {
  background: #fff3e0;
  border: 1px solid #f9a825;
  color: #6a3e00;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 12px;
  display: flex;
  gap: 10px;
  align-items: baseline;
  flex-wrap: wrap;
}

.offline-banner strong {
  font-family: Quattrocento, Georgia, serif;
  letter-spacing: 0.04em;
}

.backup-card {
  background: #fff;
  border: 1px solid #e6e3df;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
}

.export-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid #e6e3df;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
}

.export-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 220px;
}

.export-hint {
  font-size: 12px;
  color: #7a756c;
}

.export-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.export-progress {
  font-size: 13px;
  color: #555;
}

.backup-card.is-ready {
  background: #f4faf6;
  border-color: #b6dcc1;
}

.backup-card.is-running {
  background: #fffbe6;
  border-color: #f4c63d;
}

.backup-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.backup-card-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.backup-card-title strong {
  font-family: Quattrocento, Georgia, serif;
  font-size: 15px;
}

.backup-status {
  font-size: 13px;
  color: #555;
}

.backup-status.muted {
  color: #999;
  font-style: italic;
}

.backup-card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.backup-toggle {
  font-size: 13px;
  color: #555;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.backup-progress {
  margin-top: 10px;
  position: relative;
  background: #f1eee9;
  border-radius: 4px;
  height: 18px;
  overflow: hidden;
}

.backup-bar {
  background: #f9a825;
  height: 100%;
  transition: width 0.2s ease;
}

.backup-progress-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #4a3700;
}

.backup-failed-count {
  color: #c0392b;
  font-weight: 700;
}

.muted {
  color: #888;
  font-style: italic;
  text-align: center;
  padding: 40px;
}

.slot-list {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #fff;
  border: 1px solid #e6e3df;
  border-radius: 6px;
  overflow: hidden;
}

.slot-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #f1eee9;
  gap: 12px;
  flex-wrap: wrap;
}

.slot-list li:last-child {
  border-bottom: none;
}

.slot-list li.is-done {
  background: #fafaf8;
  color: #aaa;
}

.slot-list li.is-done .slot-label {
  text-decoration: line-through;
}

.slot-list li.is-current {
  background: #fff8e1;
  border-left: 4px solid #f9a825;
  padding-left: 10px;
}

.slot-list li.kind-intermission {
  background: #1a1a1a;
  color: #fff;
  font-style: italic;
  text-align: center;
  justify-content: center;
}

.slot-list li.kind-intermission.is-current {
  background: #2b2b2b;
  border-left-color: #f9a825;
  color: #fff;
}

.slot-list li.kind-intermission.is-current .slot-label {
  color: #fff;
}

.slot-list li.kind-corps {
  background: #fbf8f3;
}

.slot-list li.kind-corps.is-current {
  background: #fff8e1;
}

.slot-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 2px;
}

.block-chip {
  display: inline-block;
  align-self: flex-start;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 4px;
}

.chip-AA { background: #e3f2fd; color: #0d47a1; }
.chip-A  { background: #ede7f6; color: #4527a0; }
.chip-B  { background: #e0f2f1; color: #00695c; }
.chip-C  { background: #fff3e0; color: #e65100; }
.chip-D  { background: #fce4ec; color: #ad1457; }
.chip-corps { background: #efebe9; color: #5d4037; }

.slot-label {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
}

.slot-sub {
  font-size: 13px;
  color: #888;
}

.is-current .slot-sub {
  color: #5d4037;
  opacity: 0.85;
}

.slot-song {
  font-size: 13px;
  font-style: italic;
  color: #555;
}
.is-current .slot-song {
  color: #5d4037;
}

.slot-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.slot-notes {
  flex-basis: 100%;
  margin-top: 8px;
  padding: 12px 16px;
  background: #fff3a3;
  border: 2px solid #f9a825;
  border-radius: 6px;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(249, 168, 37, 0.25);
}
.slot-notes .notes-label {
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 800;
  color: #8a5a00;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}
.slot-notes .notes-text {
  white-space: pre-wrap;
  line-height: 1.5;
  color: #1a1a1a;
  font-weight: 500;
  font-size: 15px;
}
.slot-notes .notes-edit-btn { margin-left: 8px; }
.slot-notes .notes-textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font: inherit;
  font-size: 13px;
  background: #fff;
  resize: vertical;
}
.slot-notes .notes-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
}
.slot-add-notes {
  flex-basis: 100%;
  margin-top: 2px;
}

.slot-startpos {
  flex-basis: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  flex-wrap: wrap;
}
.startpos-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #555;
  font-weight: 700;
  letter-spacing: 0.05em;
}
.startpos-radio {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  cursor: pointer;
}
.startpos-radio input { margin: 0; }
.startpos-chip {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.startpos-onstage { background: #d4edda; color: #155724; border: 1px solid #b6dcbf; }
.startpos-offstage { background: #e6e1d4; color: #5a4a1e; border: 1px solid #d8d3c2; }
.rec-saved { color: #2e7d32; font-weight: 700; margin-left: 6px; font-size: 12px; }

.now-tag {
  font-size: 11px;
  font-weight: 700;
  background: #f9a825;
  color: #1a1a1a;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: .05em;
}

.elapsed-tag {
  display: inline-block;
  margin-left: 10px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 16px;
  font-weight: 700;
  background: #f9a825;
  color: #1a1a1a;
  padding: 2px 10px;
  border-radius: 6px;
  vertical-align: middle;
}

.cast-groups {
  margin-top: 6px;
  font-size: 12px;
  color: #555;
  line-height: 1.4;
}

.cast-group {
  margin-top: 2px;
}

.cast-role {
  color: #5d4037;
  margin-right: 4px;
}

.footer-actions {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding-bottom: 24px;
}

.completed-bar {
  margin-bottom: 10px;
}

.completed-toggle {
  background: transparent;
  border: 1px dashed #d0cbc4;
  border-radius: 6px;
  padding: 6px 14px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 13px;
  color: #888;
  cursor: pointer;
  width: 100%;
  text-align: center;
}

.completed-toggle:hover {
  color: #444;
  border-color: #aaa;
}

.completed-toggle .caret {
  margin-left: 6px;
}

.completed-list {
  margin-top: 8px;
}
</style>
